// Admin-only sub-tab inside the Intake forms mode: org-level
// intake-form template editor. Per planning doc §4 row 6, Slice 1
// shipped this as a sub-tab; Slice 4 graduates it to its own mode.
//
// Slice 2: replaces the Slice-1 JSON textareas with the
// FulfillmentTemplateItemEditor list-of-rows authoring UI. Item
// shape is locked at `{ id, label }`; ids are stable across renames
// so live form responses keyed by id don't get orphaned.

import { useState } from "react";
import {
  upsertFulfillmentIntakeTemplate,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";
import { VALID_MFG_UNIT_ORIGINS } from "@/lib/fulfillment/originEnum";
import {
  coerceTemplateItems,
  validateTemplateItems,
  type IntakeTemplateItem,
} from "@/lib/fulfillment/templateItem";
import { FulfillmentTemplateItemEditor } from "./FulfillmentTemplateItemEditor";

type Props = {
  templates: FulfillmentIntakeTemplate[];
  onRefresh: () => Promise<void>;
};

const FULFILLMENT_ORIGIN_OPTIONS = VALID_MFG_UNIT_ORIGINS.filter(
  (o) => o !== "manufactured"
);

export function FulfillmentIntakeTemplatesSubTab({ templates, onRefresh }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [tier1Items, setTier1Items] = useState<IntakeTemplateItem[]>([]);
  const [tier2Items, setTier2Items] = useState<IntakeTemplateItem[]>([]);
  const [dueDays, setDueDays] = useState(7);
  const [appliesTo, setAppliesTo] = useState<string[]>(["cash_purchase"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startNew() {
    setEditingId(null);
    setLabel("");
    setSlug("");
    setTier1Items([]);
    setTier2Items([]);
    setDueDays(7);
    setAppliesTo(["cash_purchase"]);
    setError(null);
  }

  function startEdit(t: FulfillmentIntakeTemplate) {
    setEditingId(t.id);
    setLabel(t.label);
    setSlug(t.slug);
    setTier1Items(coerceTemplateItems(t.tier_1_items));
    setTier2Items(coerceTemplateItems(t.tier_2_items));
    setDueDays(t.default_due_days);
    setAppliesTo(t.applies_to_origins);
    setError(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    const t1 = validateTemplateItems(tier1Items);
    if (!t1.ok) {
      setError(`Tier 1: ${t1.reason}`);
      setBusy(false);
      return;
    }
    const t2 = validateTemplateItems(tier2Items);
    if (!t2.ok) {
      setError(`Tier 2: ${t2.reason}`);
      setBusy(false);
      return;
    }
    const r = await upsertFulfillmentIntakeTemplate({
      id: editingId ?? undefined,
      label: label.trim(),
      slug: slug.trim().toLowerCase(),
      tier_1_items: tier1Items,
      tier_2_items: tier2Items,
      default_due_days: dueDays,
      applies_to_origins: appliesTo,
      is_active: true,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await onRefresh();
    startNew();
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Templates ({templates.length})
        </h3>
        {templates.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            No templates yet. Author one below; units entered with a matching origin will
            automatically generate a form from it.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 dark:divide-zinc-800">
            {templates.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <div className="font-medium text-gray-900 dark:text-zinc-100">{t.label}</div>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">
                    slug <code>{t.slug}</code> · applies to{" "}
                    {t.applies_to_origins.join(", ") || "(none)"} · {t.default_due_days}d due
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(t)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          {editingId ? "Edit template" : "New template"}
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">Label</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">Slug</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="cash-intake-v1"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Default due days
            </span>
            <input
              type="number"
              min={1}
              max={365}
              value={dueDays}
              onChange={(e) => setDueDays(Math.max(1, Number(e.target.value) || 7))}
              className="mt-1 w-32 rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-zinc-300">
              Applies to origins
            </legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {FULFILLMENT_ORIGIN_OPTIONS.map((o) => (
                <label key={o} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={appliesTo.includes(o)}
                    onChange={(e) => {
                      setAppliesTo((prev) =>
                        e.target.checked ? [...prev, o] : prev.filter((p) => p !== o)
                      );
                    }}
                  />
                  <code>{o}</code>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="mt-4 space-y-3">
          <FulfillmentTemplateItemEditor
            tier={1}
            items={tier1Items}
            onChange={setTier1Items}
            disabled={busy}
          />
          <FulfillmentTemplateItemEditor
            tier={2}
            items={tier2Items}
            onChange={setTier2Items}
            disabled={busy}
          />
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        <div className="mt-3 flex justify-end gap-2">
          {editingId ? (
            <button
              type="button"
              onClick={startNew}
              disabled={busy}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            >
              Cancel edit
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || !label.trim() || !slug.trim()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : editingId ? "Save changes" : "Create template"}
          </button>
        </div>
      </div>
    </div>
  );
}
